import { create } from "zustand";

/**
 * Rascunho da seleção que monta a sessão de revisão: matéria, palavra-chave,
 * baralho e modo de estudo (antecipar, limite, ordem da fila) — as quatro
 * dimensões independentes do requirement "A revisão é montada por dimensões
 * independentes" (revisao-home/spec.md).
 *
 * Persistida sob chave própria (`STORAGE_KEY`), nunca em `filters.*` — a
 * chave do carrossel. É exatamente o risco que o CLAUDE.md já registra para
 * o `<Filter>` em telas de revisão: mostrá-lo numa tela de cartões
 * reescreveria em silêncio os filtros do carrossel do aluno (por isso as
 * rotas de Revisão usam `DefaultLayout hideFilter`). Esta store não importa
 * `useQuestionFiltersStore`, e nada aqui escreve nas chaves `filters.*`.
 *
 * Mesmos nomes e mesma gramática de `useQuestionFiltersStore` (design.md da
 * change `flashcards-revisao-redesign`, Decisão 2) — toggle/remove por
 * dimensão, hydrate idempotente, clearAll — mas sem acoplar a ela: os dois
 * arquivos não se importam um ao outro, e divergem onde as dimensões
 * divergem (aqui não há draft/applied: não existe uma segunda cópia "em
 * vigor" para a seleção da revisão discordar de, como `filterQuery` é para o
 * carrossel — "iniciar" apenas lê a seleção corrente, Decisão 3).
 *
 * Zero chamadas de API: esta store só guarda e persiste estado. Montar a
 * fila (`GET /review/due`) é responsabilidade de quem consome a seleção —
 * nunca desta store (design.md, Decisão 3).
 */

export type RevisaoListDimension = "subjects" | "keywords" | "decks";

export const REVISAO_LIST_DIMENSIONS: RevisaoListDimension[] = [
  "subjects",
  "keywords",
  "decks",
];

/** Prefix shown on a removable selected-filter token (revisao-home/spec.md, "Os filtros escolhidos são visíveis e removíveis um a um"). */
export const REVISAO_DIMENSION_LABEL: Record<RevisaoListDimension, string> = {
  subjects: "Matéria",
  keywords: "Palavra-chave",
  decks: "Baralho",
};

/**
 * Sem contrato de API nem frame que enumere os valores — "ordem da fila" só
 * existe como nome, em proposal.md Suposição 3. `ModoDeEstudoSecao` usa este
 * conjunto fechado. `due` reproduz a ordem que a API já devolve hoje (nenhuma
 * reordenação); `random` só embaralha a fila já carregada. `hardest` é o mais
 * arriscado dos três: o CLAUDE.md descreve `ef`/`lapses` como campos SM-2 que
 * a API também enviaria, mas `ReviewCard` (`sdk/@types/Revisao.ts`) não os
 * declara hoje — usar `hardest` de verdade exige primeiro confirmar isso
 * contra a API ao vivo e só então acrescentar os campos ao tipo.
 */
export type QueueOrder = "due" | "random" | "hardest";

export const QUEUE_ORDERS: QueueOrder[] = ["due", "random", "hardest"];

/** RevisaoEstudar.view.tsx's existing default (design.md, Open Questions — "o valor inicial fica em 20"). */
export const DEFAULT_SESSION_LIMIT = 20;

export interface RevisaoSelecao {
  subjects: string[];
  keywords: string[];
  decks: string[];
  ahead: boolean;
  limit: number;
  queueOrder: QueueOrder;
}

export interface SelectedRevisaoToken {
  dimension: RevisaoListDimension;
  dimensionLabel: string;
  value: string;
}

export const EMPTY_SELECAO: RevisaoSelecao = {
  subjects: [],
  keywords: [],
  decks: [],
  ahead: false,
  limit: DEFAULT_SESSION_LIMIT,
  queueOrder: "due",
};

/** Própria da revisão — não é `filters.*`, a família de chaves do carrossel. */
const STORAGE_KEY = "revisao.selecao";

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function isQueueOrder(value: unknown): value is QueueOrder {
  return QUEUE_ORDERS.includes(value as QueueOrder);
}

function readSelecao(): RevisaoSelecao {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SELECAO;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY_SELECAO;

    return {
      subjects: asStringList(parsed.subjects),
      keywords: asStringList(parsed.keywords),
      decks: asStringList(parsed.decks),
      ahead: typeof parsed.ahead === "boolean" ? parsed.ahead : EMPTY_SELECAO.ahead,
      limit:
        typeof parsed.limit === "number" && parsed.limit > 0
          ? parsed.limit
          : DEFAULT_SESSION_LIMIT,
      queueOrder: isQueueOrder(parsed.queueOrder) ? parsed.queueOrder : "due",
    };
  } catch {
    return EMPTY_SELECAO;
  }
}

function persist(selecao: RevisaoSelecao) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selecao));
  } catch {
    // localStorage pode lançar (modo privado, cota excedida) — a seleção
    // segue válida em memória pelo resto da sessão.
  }
}

/** Fichas removíveis uma a uma, cada uma nomeando a sua dimensão (revisao-home/spec.md). */
export function selectionTokens(selecao: RevisaoSelecao): SelectedRevisaoToken[] {
  return REVISAO_LIST_DIMENSIONS.flatMap((dimension) =>
    selecao[dimension].map((value) => ({
      dimension,
      dimensionLabel: REVISAO_DIMENSION_LABEL[dimension],
      value,
    })),
  );
}

interface RevisaoSelecaoState {
  selecao: RevisaoSelecao;
  hydrated: boolean;

  hydrate: () => void;
  toggle: (dimension: RevisaoListDimension, value: string) => void;
  remove: (dimension: RevisaoListDimension, value: string) => void;
  setAhead: (ahead: boolean) => void;
  setLimit: (limit: number) => void;
  setQueueOrder: (queueOrder: QueueOrder) => void;
  clearAll: () => void;
}

const useRevisaoSelecaoStore = create<RevisaoSelecaoState>((set, get) => ({
  selecao: EMPTY_SELECAO,
  hydrated: false,

  hydrate: () => {
    if (get().hydrated) return;
    set({ selecao: readSelecao(), hydrated: true });
  },

  toggle: (dimension, value) =>
    set((state) => {
      const current = state.selecao[dimension];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const selecao = { ...state.selecao, [dimension]: next };
      persist(selecao);
      return { selecao };
    }),

  remove: (dimension, value) =>
    set((state) => {
      const selecao = {
        ...state.selecao,
        [dimension]: state.selecao[dimension].filter((v) => v !== value),
      };
      persist(selecao);
      return { selecao };
    }),

  setAhead: (ahead) =>
    set((state) => {
      const selecao = { ...state.selecao, ahead };
      persist(selecao);
      return { selecao };
    }),

  setLimit: (limit) =>
    set((state) => {
      const selecao = { ...state.selecao, limit };
      persist(selecao);
      return { selecao };
    }),

  setQueueOrder: (queueOrder) =>
    set((state) => {
      const selecao = { ...state.selecao, queueOrder };
      persist(selecao);
      return { selecao };
    }),

  // "Um comando único de limpeza descarta toda a seleção de uma vez" — as
  // quatro dimensões juntas, inclusive modo de estudo, não só as três listas.
  clearAll: () =>
    set(() => {
      persist(EMPTY_SELECAO);
      return { selecao: EMPTY_SELECAO };
    }),
}));

export default useRevisaoSelecaoStore;
